import { useEffect, useMemo, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Textarea } from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import type { AccountAction, UserAccountStatus } from "../types";
import { formatLabel, legalAccountActions } from "../labels";

const ACTIONS: AccountAction[] = ["activate", "restore", "close"];

export interface AccountActionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    action: AccountAction;
    reason: string;
    approvalId?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
  /** Current status: only the actions legal from it are offered. */
  status?: UserAccountStatus;
}

export function AccountActionDialog({
  status,
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: AccountActionDialogProps) {
  const actions = useMemo(
    () => (status ? legalAccountActions(status) : ACTIONS),
    [status],
  );
  const [action, setAction] = useState<AccountAction>(actions[0] ?? "close");
  useEffect(() => {
    if (!actions.includes(action)) setAction(actions[0] ?? "close");
  }, [actions, action]);
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  async function handleSubmit() {
    await onSubmit({
      action,
      reason,
      ...(approvalId.trim() ? { approvalId: approvalId.trim() } : {}),
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Account action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={action === "ban" || action === "close" ? "danger" : "primary"}
            loading={busy}
            disabled={reason.trim().length < 3 || actions.length === 0}
            onClick={() => void handleSubmit()}
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Activate, restore or close an account here. To warn, restrict (1 to
          48 hours), suspend (1 to 30 days) or ban someone, act on a report in
          Trust and Safety, or file a staff report there first. That keeps the
          reason, the length and the record together, and the person sees it in
          Account standing.
        </Text>
        {actions.length === 0 ? (
          <Alert tone="info" title="No further actions">
            A {status ? formatLabel(status) : "closed"} account cannot change
            status.
          </Alert>
        ) : null}
        <label>
          <Text role="label" as="span">
            Action
          </Text>
          <select
            className="huddl-native-select"
            value={action}
            onChange={(event) => setAction(event.target.value as AccountAction)}
            style={{
              width: "100%",
              marginTop: "var(--space-4)",
              padding: "var(--space-8) var(--space-12)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--color-border-default)",
              background: "var(--color-surface-raised)",
            }}
          >
            {actions.map((item) => (
              <option key={item} value={item}>
                {formatLabel(item)}
              </option>
            ))}
          </select>
        </label>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          required
        />
        <ApprovalPickerField
          label="Approved Approval (required for ban / governed restores)"
          value={approvalId}
          onChange={setApprovalId}
          minAuthority="dual"
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
